import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, fmtINR, STATUS_STYLE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Search } from "lucide-react";

export default function PurchaseOrders() {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;

  useEffect(() => {
    setLoading(true);
    api.get("/po")
      .then(({ data }) => setPos(data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return pos.filter(p => !q || p.po_number.toLowerCase().includes(q.toLowerCase()) || p.dealer_name.toLowerCase().includes(q.toLowerCase()));
  }, [pos, q]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const paginatedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  useEffect(() => { setPage(1); }, [q]);

  return (
    <div className="space-y-6" data-testid="po-list-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">All Purchase Orders</div>
        <h1 className="mt-1 text-3xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>Purchase Orders</h1>
      </div>
      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search PO number or dealer..." value={q} onChange={e=>setQ(e.target.value)} className="pl-9" data-testid="po-search" />
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 whitespace-nowrap">PO Number</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Dealer</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Area</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Created by</th>
              <th className="text-right px-4 py-3 whitespace-nowrap">Total</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Status</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-36" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-16" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-28" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-20 ml-auto" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-5 w-20" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
                </tr>
              ))
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-16 text-slate-500">No purchase orders.</td></tr>
            ) : (
              paginatedItems.map(p => {
                const s = STATUS_STYLE[p.status] || STATUS_STYLE.draft;
                return (
                  <tr key={p.po_id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-xs"><Link to={`/po/${p.po_id}`} className="text-blue-700 hover:underline" data-testid={`po-row-${p.po_number}`}>{p.po_number}</Link></td>
                    <td className="px-4 py-3 whitespace-nowrap">{p.dealer_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-600">{p.dealer_area}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-600">{p.created_by_name} <span className="text-xs text-slate-400">({p.created_by_role})</span></td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">{fmtINR(p.total)}</td>
                    <td className="px-4 py-3 whitespace-nowrap"><Badge variant="outline" className={`${s.bg} ${s.text} ${s.border}`}>{s.label}</Badge></td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString()}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
        {filtered.length > PAGE_SIZE && (
          <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="text-xs text-slate-500">
              Showing <span className="font-medium">{(page - 1) * PAGE_SIZE + 1}</span> to <span className="font-medium">{Math.min(page * PAGE_SIZE, filtered.length)}</span> of <span className="font-medium">{filtered.length}</span> POs (100 per page)
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-8 text-xs">Previous</Button>
              <span className="text-xs text-slate-600 px-2 font-medium">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-8 text-xs">Next</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
